import { useState } from "react";
import { App, Button, Form, Input, Popconfirm, Select, Space, Tooltip } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AffiliateNetwork } from "@glow-price/shared";
import {
  AFFILIATE_NETWORK_LABELS_CN,
  AFFILIATE_NETWORK_TYPES,
  ENTITY_STATUSES,
} from "@glow-price/shared";

import { affiliateNetworksApi } from "../api/resources";
import { CrudDrawer } from "../components/CrudDrawer";
import { EmptyState, EnhancedTable, PageHeader } from "../components";

// Phase 6B: only DUOMAI + TENCENT_YOUXUAN remain. Other historical types
// (manual_settlement / private_cps / tbk / jd_union / pdd_duoke) are gone.
const TYPE_OPTIONS = Object.values(AFFILIATE_NETWORK_TYPES).map((v) => ({
  value: v,
  label: AFFILIATE_NETWORK_LABELS_CN[v],
}));
const STATUS_OPTIONS = Object.values(ENTITY_STATUSES).map((v) => ({ value: v, label: v }));

export function AffiliateNetworksPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<AffiliateNetwork | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const list = useQuery({
    queryKey: ["affiliate-networks", page],
    queryFn: () => affiliateNetworksApi.list({ page, pageSize: 20 }),
  });

  const saveMutation = useMutation({
    mutationFn: async (values: Partial<AffiliateNetwork> & { apiConfig?: string | Record<string, unknown> }) => {
      const payload: Partial<AffiliateNetwork> = {
        ...values,
        apiConfig:
          typeof values.apiConfig === "string"
            ? safeParseJson(values.apiConfig)
            : values.apiConfig,
      };
      if (editing) return affiliateNetworksApi.update(editing.id, payload);
      return affiliateNetworksApi.create(payload);
    },
    onSuccess: () => {
      message.success("已保存");
      qc.invalidateQueries({ queryKey: ["affiliate-networks"] });
      setDrawerOpen(false);
      setEditing(null);
    },
    onError: (err: Error) => message.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => affiliateNetworksApi.remove(id),
    onSuccess: () => {
      message.success("已删除");
      qc.invalidateQueries({ queryKey: ["affiliate-networks"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  const items = list.data?.items ?? [];
  const showEmpty = !list.isLoading && items.length === 0;

  return (
    <div>
      <PageHeader
        title="联盟平台"
        description="配置联盟 CPS 平台（多麦、腾讯优选等）及其 API 凭证，供渠道联盟拉单使用。"
        stats={[{ label: `共 ${list.data?.total ?? 0} 个平台` }]}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              setDrawerOpen(true);
            }}
          >
            新建联盟
          </Button>
        }
      />

      {showEmpty ? (
        <EmptyState
          title="暂无联盟平台"
          description="尚未配置联盟。点击「新建联盟」开始添加，如多麦、腾讯优选等。"
          action={
            <Button
              type="primary"
              onClick={() => {
                setEditing(null);
                setDrawerOpen(true);
              }}
            >
              新建联盟
            </Button>
          }
        />
      ) : (
        <EnhancedTable<AffiliateNetwork>
          rowKey="id"
          loading={list.isLoading}
          dataSource={items}
          persistKey="affiliate-networks"
          exportFilename="affiliate-networks"
          pagination={{
            current: page,
            pageSize: 20,
            total: list.data?.total ?? 0,
            onChange: (p) => setPage(p),
            showSizeChanger: false,
          }}
          columns={[
            { title: "名称", dataIndex: "name" },
            { title: "代号", dataIndex: "code", width: 160 },
            {
              title: "类型",
              dataIndex: "type",
              width: 200,
              render: (v: AffiliateNetwork["type"]) =>
                AFFILIATE_NETWORK_LABELS_CN[v] ?? v,
            },
            { title: "状态", dataIndex: "status", width: 100 },
            {
              title: "操作",
              key: "actions",
              width: 110,
              render: (_, row) => (
                <Space size={4}>
                  <Tooltip title="编辑">
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setEditing(row);
                        setDrawerOpen(true);
                      }}
                    />
                  </Tooltip>
                  <Popconfirm title="确认删除？" onConfirm={() => deleteMutation.mutate(row.id)}>
                    <Tooltip title="删除">
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Tooltip>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      )}

      <CrudDrawer
        open={drawerOpen}
        title={editing ? "编辑联盟" : "新建联盟"}
        initialValues={
          (editing
            ? { ...editing, apiConfig: JSON.stringify(editing.apiConfig, null, 2) }
            : {
                type: AFFILIATE_NETWORK_TYPES.DUOMAI,
                status: ENTITY_STATUSES.ACTIVE,
                apiConfig: "{}",
              }) as unknown as Partial<AffiliateNetwork>
        }
        loading={saveMutation.isPending}
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
        onSubmit={(values) =>
          saveMutation.mutateAsync(values as unknown as Partial<AffiliateNetwork>)
        }
      >
        {() => (
          <>
            <Form.Item name="name" label="名称" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="code" label="代号" rules={[{ required: true }]}>
              <Input placeholder="duomai / tencent_youxuan" />
            </Form.Item>
            <Form.Item name="type" label="类型" rules={[{ required: true }]}>
              <Select options={TYPE_OPTIONS} />
            </Form.Item>
            <Form.Item
              name="apiConfig"
              label="API 配置 (JSON)"
              rules={[
                {
                  validator: (_, v) => {
                    if (!v) return Promise.resolve();
                    try {
                      JSON.parse(v);
                      return Promise.resolve();
                    } catch {
                      return Promise.reject(new Error("不是合法 JSON"));
                    }
                  },
                },
              ]}
            >
              <Input.TextArea rows={4} placeholder='{"appKey":"..."}' />
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Select options={STATUS_OPTIONS} />
            </Form.Item>
          </>
        )}
      </CrudDrawer>
    </div>
  );
}

function safeParseJson(raw: string): Record<string, unknown> {
  try {
    const v = JSON.parse(raw);
    return v && typeof v === "object" && !Array.isArray(v) ? v : {};
  } catch {
    return {};
  }
}
